import { useEffect, useState } from "react";
import { Evento } from "../../../../data/portal-data";
import { portalEventosServices } from "../../../../services/portal-eventos.services";
import "./eventos.scss";

const TIPO_CONFIG: Record<
  string,
  { label: string; icon: string; color: string }
> = {
  CURSO: { label: "Curso", icon: "bi-mortarboard-fill", color: "cyan" },
  EVENTO: { label: "Evento", icon: "bi-calendar-event-fill", color: "green" },
  NOVEDAD: { label: "Novedad", icon: "bi-megaphone-fill", color: "amber" },
};

const DEFAULT_TIPO = {
  label: "Evento",
  icon: "bi-calendar-event-fill",
  color: "green",
};

/** Normaliza el tipo que llega del backend a mayúsculas */
function normalizarTipo(tipo: string): string {
  return tipo?.toUpperCase() ?? "EVENTO";
}

function truncar(texto: string, max: number) {
  return texto.length > max ? texto.slice(0, max).trimEnd() + "…" : texto;
}

function formatFecha(iso: string) {
  const [y, m, d] = iso.split("-");
  const meses = [
    "ene",
    "feb",
    "mar",
    "abr",
    "may",
    "jun",
    "jul",
    "ago",
    "sep",
    "oct",
    "nov",
    "dic",
  ];
  return { dia: d, mes: meses[parseInt(m) - 1], anio: y };
}

export default function Eventos() {
  const [eventos, setEventos] = useState<Evento[]>([]);

  useEffect(() => {
    portalEventosServices
      .getVigentes()
      .then((res) => setEventos(res.data))
      .catch(() => setEventos([]));
  }, []);

  return (
    <section id="eventos" className="section eventos-page">
      <div className="container section-title fade-up text-center mb-5">
        <h2>Cursos, Eventos y Novedades</h2>
        <p>
          Manténgase informado sobre las últimas actividades, capacitaciones y
          noticias del Colegio de Fonoaudiólogos de La Pampa.
        </p>
      </div>

      <div className="container fade-up">
        {/* Filtro visual por tipo */}
        <div className="tipo-badges mb-5">
          <span className="tipo-badge cyan">
            <i className="bi bi-mortarboard-fill"></i> Cursos
          </span>
          <span className="tipo-badge green">
            <i className="bi bi-calendar-event-fill"></i> Eventos
          </span>
          <span className="tipo-badge amber">
            <i className="bi bi-megaphone-fill"></i> Novedades
          </span>
        </div>

        <div className="row gy-4">
          {eventos.map((ev) => {
            const tipoKey = normalizarTipo(ev.tipo);
            const cfg = TIPO_CONFIG[tipoKey] ?? DEFAULT_TIPO;
            const fecha = ev.fecha
              ? formatFecha(ev.fecha.toString().slice(0, 10))
              : null;
            return (
              <div key={ev.id} className="col-lg-4 col-md-6">
                <div className={`evento-card evento-card--${cfg.color}`}>
                  {/* Header coloreado — siempre visible */}
                  <div className={`evento-fecha fecha--${cfg.color}`}>
                    {fecha ? (
                      <>
                        <span className="dia">{fecha.dia}</span>
                        <span className="mes">{fecha.mes}</span>
                        <span className="anio">{fecha.anio}</span>
                      </>
                    ) : (
                      <i
                        className={`bi ${cfg.icon}`}
                        style={{
                          fontSize: "2rem",
                          color: "white",
                          opacity: 0.9,
                        }}
                      ></i>
                    )}
                  </div>

                  {/* Contenido */}
                  <div className="evento-body">
                    <div className="evento-header">
                      <span className={`evento-tipo tipo--${cfg.color}`}>
                        <i className={`bi ${cfg.icon}`}></i>
                        {cfg.label}
                      </span>
                      {ev.badge && (
                        <span className="evento-badge">{ev.badge}</span>
                      )}
                    </div>

                    <h4 className="evento-titulo">{truncar(ev.titulo, 80)}</h4>
                    <p className="evento-desc">
                      {truncar(ev.descripcion, 220)}
                    </p>

                    {(ev.hora || ev.lugar) && (
                      <div className="evento-meta">
                        {ev.hora && (
                          <span>
                            <i className="bi bi-clock"></i> {ev.hora}
                          </span>
                        )}
                        {ev.lugar && (
                          <span>
                            <i className="bi bi-geo-alt"></i>{" "}
                            {truncar(ev.lugar, 100)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="eventos-footer fade-up">
          <i className="bi bi-info-circle"></i>
          Para más información o inscripciones comunicarse al{" "}
          <strong>02954-702284</strong> o por email a{" "}
          <strong>cofolp@gmail.com</strong>
        </div>
      </div>
    </section>
  );
}
